import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import { useWebsiteData } from '../../context/WebsiteDataContext';
import { Link } from 'react-router-dom';

const ProjectsPage = () => {
  const { getActiveProjects } = useWebsiteData();
  const projects = getActiveProjects();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const categories = [
    { id: 'all', name: 'All Projects', count: projects.length },
    { id: 'residential', name: 'Residential', count: projects.filter(p => p.category === 'residential').length },
    { id: 'commercial', name: 'Commercial', count: projects.filter(p => p.category === 'commercial').length },
    { id: 'industrial', name: 'Industrial', count: projects.filter(p => p.category === 'industrial').length },
    { id: 'interior', name: 'Interior Design', count: projects.filter(p => p.category === 'interior').length }
  ];

  const statuses = [
    { id: 'all', name: 'All Status', count: projects.length },
    { id: 'completed', name: 'Completed', count: projects.filter(p => p.status === 'completed').length },
    { id: 'in-progress', name: 'In Progress', count: projects.filter(p => p.status === 'in-progress').length }
  ];

  const filteredProjects = projects.filter(project => {
    const categoryMatch = selectedCategory === 'all' || project.category === selectedCategory;
    const statusMatch = selectedStatus === 'all' || project.status === selectedStatus;
    return categoryMatch && statusMatch;
  });

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'residential': return 'Home';
      case 'commercial': return 'Building';
      case 'industrial': return 'Factory';
      case 'interior': return 'Palette';
      default: return 'Building';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Our Projects Portfolio
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Discover our diverse portfolio of successful construction and design projects across Pune and Maharashtra
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-green-500" />
                <span>{projects.filter(p => p.status === 'completed').length} Completed Projects</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={16} className="text-primary" />
                <span>Pune & Maharashtra</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Star" size={16} className="text-yellow-500" />
                <span>5-Star Client Ratings</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-card border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-4 justify-center">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                  }`}
                >
                  {category.name}
                  <span className="ml-2 text-xs opacity-75">({category.count})</span>
                </button>
              ))}
            </div>

            {/* Status Filter */}
            <div className="flex flex-wrap gap-2">
              {statuses.map((status) => (
                <button
                  key={status.id}
                  onClick={() => setSelectedStatus(status.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedStatus === status.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                  }`}
                >
                  {status.name}
                  <span className="ml-2 text-xs opacity-75">({status.count})</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {filteredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project) => (
                <div key={project.id} className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  {/* Project Image */}
                  <div className="relative h-48 overflow-hidden">
                    <Image
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4 flex space-x-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                        {project.status === 'completed' ? 'Completed' : 'In Progress'}
                      </span>
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                        {project.category.charAt(0).toUpperCase() + project.category.slice(1)}
                      </span>
                    </div>
                    {project.rating && (
                      <div className="absolute top-4 right-4 flex items-center space-x-1 bg-white/90 rounded-full px-2 py-1">
                        <Icon name="Star" size={12} className="text-yellow-500 fill-current" />
                        <span className="text-xs font-medium">{project.rating}</span>
                      </div>
                    )}
                  </div>

                  {/* Project Content */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-xl font-bold text-foreground line-clamp-2">{project.title}</h3>
                      <Icon name={getCategoryIcon(project.category)} size={20} className="text-primary flex-shrink-0 ml-2" />
                    </div>

                    <p className="text-muted-foreground mb-4 line-clamp-3">{project.description}</p>

                    {/* Project Details */}
                    <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Location:</span>
                        <div className="font-medium">{project.location}</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Area:</span>
                        <div className="font-medium">{project.area}</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Duration:</span>
                        <div className="font-medium">{project.duration}</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Budget:</span>
                        <div className="font-medium">{project.budget}</div>
                      </div>
                    </div>

                    {/* Services */}
                    <div className="mb-4">
                      <span className="text-sm text-muted-foreground">Services:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {project.services.slice(0, 3).map((service, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full"
                          >
                            {service}
                          </span>
                        ))}
                        {project.services.length > 3 && (
                          <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                            +{project.services.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Highlights */}
                    <div className="mb-4">
                      <span className="text-sm text-muted-foreground">Highlights:</span>
                      <ul className="mt-1 space-y-1">
                        {project.highlights.slice(0, 3).map((highlight, index) => (
                          <li key={index} className="text-xs text-muted-foreground flex items-center space-x-1">
                            <Icon name="Check" size={10} className="text-green-500 flex-shrink-0" />
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Testimonial */}
                    {project.testimonial && (
                      <div className="mb-4 p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <Icon name="Quote" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-muted-foreground italic line-clamp-3">
                            "{project.testimonial}"
                          </p>
                        </div>
                        <div className="text-xs text-muted-foreground mt-2">
                          - {project.client}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      <Link to={`/project/${project.id}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          fullWidth
                          iconName="Eye"
                          iconPosition="left"
                        >
                          View Details
                        </Button>
                      </Link>
                      <Button
                        variant="default"
                        size="sm"
                        fullWidth
                        iconName="MessageSquare"
                        iconPosition="left"
                      >
                        Get Quote
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Icon name="Search" size={64} className="mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No projects found</h3>
              <p className="text-muted-foreground">
                Try adjusting your filters to see more projects
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-muted/20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Let's discuss your vision and create something amazing together. Our team is ready to bring your ideas to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="default"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => window.dispatchEvent(new Event('open-consultation-modal'))}
            >
              Book Free Consultation
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="MessageCircle"
              iconPosition="left"
            >
              Chat on WhatsApp
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectsPage; 